import React, { useEffect, useState } from 'react';
import { Button, Divider, Image } from 'antd';
import { useLocation } from 'react-router-dom';

import { Loader } from '../components/Loader.js';
import { FlexRow } from '../components/FlexRow.js';
import { FormCard } from '../components/FormCard.js';
import { CardTitle } from '../components/CardTitle.js';
import { AntButton } from '../components/SubmitButton.js';
import { callFetch } from '../helpers/fetch.js';
import { FormError } from '../components/FormError.js';
import { FlexColumn } from '../components/FlexColumn.js';
import { SSO_API_URL } from '../sso-secrets.js';
import { NotFoundPage } from './NotFoundPage.js';
import { InputField, SelectField } from '../components/Field.js';

export function ProfilePage() {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isNotSignedIn, setIsNotSignedIn] = useState(false);
  const [error, setError] = useState(null);
  const [showSaveButton, setShowSaveButton] = useState(false);
  const [user, setUser] = useState(null);
  // form data fields
  const [email, setEmail] = useState(null);
  const [gender, setGender] = useState('male');
  const [lastName, setLastName] = useState(null);
  const [firstName, setFirstName] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [defaultUsername, setDefaultUsername] = useState(null);
  const [
    isUserApplicationUsernamesChanged,
    setIsUserApplicationUsernamesChanged,
  ] = useState(false);
  const [userApplicationUsernames, setUserApplicationUsernames] = useState([]);

  const params = new URLSearchParams(location.search);
  const redirectURL = params.get('redirect_url');

  const onFirstNameChange = (event) => setFirstName(event.target.value);
  const onLastNameChange = (event) => setLastName(event.target.value);
  const onEmailChange = (event) => setEmail(event.target.value);
  const onGenderChange = (value) => setGender(value);
  const onImageUrlChange = (event) => setImageUrl(event.target.value);
  const onDefaultUsernameChange = (event) =>
    setDefaultUsername(event.target.value);
  const onUserApplicationUsernameChange = (appId, appName) => (event) => {
    setIsUserApplicationUsernamesChanged(true);

    const otherUserApplicationUsernames = userApplicationUsernames.filter(
      (applicationUsername) => applicationUsername.applicationId !== appId,
    );

    const newUserApplicationUsernames = [
      ...otherUserApplicationUsernames,
      {
        applicationId: appId,
        applicationName: appName,
        value: event.target.value,
      },
    ].sort((a, b) => (a.applicationName > b.applicationName ? 1 : -1));

    setUserApplicationUsernames(newUserApplicationUsernames);
  };

  useEffect(() => {
    const getUser = async () => {
      const response = await callFetch(`${SSO_API_URL}/api/sso-client/profile`);

      setLoading(false);

      if (!response?.ok) {
        setIsNotSignedIn(true);
        return;
      }

      setUser(response.user);
    };

    getUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName);
      setLastName(user.lastName);
      setEmail(user.email);
      setGender(user.data.gender);
      setImageUrl(user.imageUrl);
      setDefaultUsername(user.username);
      setUserApplicationUsernames(
        user.registrations
          .map((registration) => ({
            applicationId: registration.applicationId,
            applicationName: registration.applicationName,
            value: registration.username,
          }))
          .sort((a, b) => (a.applicationName > b.applicationName ? 1 : -1)),
      );
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      const shouldShowSaveButton =
        firstName !== user.firstName ||
        lastName !== user.lastName ||
        email !== user.email ||
        gender !== user?.data?.gender ||
        imageUrl !== user?.imageUrl ||
        defaultUsername !== user.username ||
        isUserApplicationUsernamesChanged;

      setShowSaveButton(shouldShowSaveButton);
    }
  }, [
    user,
    firstName,
    lastName,
    email,
    gender,
    imageUrl,
    defaultUsername,
    isUserApplicationUsernamesChanged,
  ]);

  const onBackToPortal = () => {
    if (redirectURL) {
      window.location.href = redirectURL;
    }
  };

  const onProfileSave = async () => {
    try {
      setSubmitting(true);

      const response = await callFetch(
        `${SSO_API_URL}/api/sso-client/profile`,
        {
          method: 'post',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            gender,
            lastName,
            firstName,
            imageUrl,
            defaultUsername,
            userApplicationUsernames,
          }),
        },
      );

      setSubmitting(false);

      setIsUserApplicationUsernamesChanged(false);

      if (!response?.ok) {
        setError(response?.error);
        return;
      }

      if (response?.ok) {
        setError(null);

        const response = await callFetch(
          `${SSO_API_URL}/api/sso-client/profile`,
        );

        if (response?.ok) {
          setUser(response.user);
        }
      }
    } catch (error) {
      console.log(`Sign In spage: ${error}`);
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (isNotSignedIn) {
    return <NotFoundPage />;
  }

  return (
    <FormCard width="50%">
      <CardTitle margin="0 0 20px 0">{user?.fullName} Profile</CardTitle>
      {error && <FormError>{error}</FormError>}
      <FlexRow>
        <Image
          width={200}
          height={200}
          src={user?.imageUrl || 'error'}
          fallback="data:image/png;base64,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"
        />
        <FlexColumn margin="0 0 0 20px">
          <Divider orientation="left" style={{ margin: '0 0 16px 0' }}>
            General Info
          </Divider>
          <InputField
            label="First Name"
            value={firstName}
            onChange={onFirstNameChange}
          />
          <InputField
            label="Last Name"
            value={lastName}
            onChange={onLastNameChange}
          />
          <InputField label="Email" value={email} onChange={onEmailChange} />
          <SelectField
            label="Gender"
            value={gender}
            onChange={onGenderChange}
            options={[
              { value: 'male', label: 'Male' },
              { value: 'female', label: 'Female' },
            ]}
          />
          <InputField
            label="Image Url"
            value={imageUrl}
            onChange={onImageUrlChange}
          />
          <Divider orientation="left">Nicknames</Divider>
          <InputField
            label="Default"
            value={defaultUsername}
            onChange={onDefaultUsernameChange}
          />
          {userApplicationUsernames.map((userApplicationUsername) => (
            <InputField
              key={userApplicationUsername.applicationId}
              label={userApplicationUsername.applicationName}
              value={userApplicationUsername.value}
              onChange={onUserApplicationUsernameChange(
                userApplicationUsername.applicationId,
                userApplicationUsername.applicationName,
              )}
            />
          ))}
        </FlexColumn>
      </FlexRow>
      <FlexRow margin="10px 0 0 0" justify="space-between">
        <AntButton
          style={{ padding: '0' }}
          type="link"
          onClick={onBackToPortal}
        >
          {`< back to Portal`}
        </AntButton>
        {showSaveButton && (
          <Button type="primary" loading={submitting} onClick={onProfileSave}>
            Save
          </Button>
        )}
      </FlexRow>
    </FormCard>
  );
}
